import { useCallback, useReducer, useRef } from 'react';
import { Toast } from './Toast';
import type { ToastData, ToastVariant } from './Toast';
import { ToastContext } from './ToastContext';
import type { ToastFn } from './ToastContext';

const MAX_TOASTS = 3;
const AUTO_DISMISS_MS: Record<ToastVariant, number> = {
  success: 4000,
  info: 4000,
  warning: 6000,
  error: 6000,
};

export interface ToastInput {
  variant: ToastVariant;
  title: string;
  description?: string;
  /** Auto-dismiss delay in ms. Omit to use the per-variant default. */
  durationMs?: number;
  /** Dedupe key: a toast with the same id replaces the visible one instead of stacking. */
  id?: string;
}

type ToastAction =
  | { type: 'ADD_TOAST'; toast: ToastData }
  | { type: 'REMOVE_TOAST'; id: string };

function toastReducer(state: ToastData[], action: ToastAction): ToastData[] {
  switch (action.type) {
    case 'ADD_TOAST': {
      // Same id (caller-supplied dedupe key): replace in place instead of stacking a duplicate.
      if (state.some((t) => t.id === action.toast.id)) {
        return state.map((t) => (t.id === action.toast.id ? action.toast : t));
      }
      const next = [...state, action.toast];
      // Enforce max visible toasts — drop oldest
      if (next.length > MAX_TOASTS) {
        return next.slice(next.length - MAX_TOASTS);
      }
      return next;
    }
    case 'REMOVE_TOAST':
      return state.filter((t) => t.id !== action.id);
  }
}

let toastCounter = 0;

export function ToastProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [toasts, dispatch] = useReducer(toastReducer, []);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const removeToast = useCallback((id: string) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    dispatch({ type: 'REMOVE_TOAST', id });
  }, []);

  const addToast = useCallback(
    (input: ToastInput) => {
      const { durationMs, id: requestedId, ...data } = input;
      const id = requestedId ?? `toast-${++toastCounter}`;
      const toast: ToastData = { id, ...data };

      // A repeated id replaces the visible toast (e.g. one "Too many requests" per retried
      // HTTP attempt must not stack), so restart its dismiss clock rather than leak the old one.
      const existingTimer = timersRef.current.get(id);
      if (existingTimer) {
        clearTimeout(existingTimer);
      }

      dispatch({ type: 'ADD_TOAST', toast });

      // Auto-dismiss timer
      const timer = setTimeout(() => {
        removeToast(id);
      }, durationMs ?? AUTO_DISMISS_MS[input.variant]);

      timersRef.current.set(id, timer);
    },
    [removeToast],
  );

  // Build the toast function with shorthand methods
  const toastFn = useCallback(
    (input: ToastInput) => addToast(input),
    [addToast],
  ) as ToastFn;

  toastFn.success = useCallback(
    (title: string, description?: string) => addToast({ variant: 'success', title, description }),
    [addToast],
  );
  toastFn.error = useCallback(
    (title: string, description?: string) => addToast({ variant: 'error', title, description }),
    [addToast],
  );
  toastFn.warning = useCallback(
    (title: string, description?: string) => addToast({ variant: 'warning', title, description }),
    [addToast],
  );
  toastFn.info = useCallback(
    (title: string, description?: string) => addToast({ variant: 'info', title, description }),
    [addToast],
  );

  return (
    <ToastContext.Provider value={toastFn}>
      {children}

      {/* Toast container — above BottomNav on mobile, standard position on desktop */}
      <div
        aria-live="polite"
        aria-relevant="additions removals"
        className="fixed bottom-20 left-1/2 z-50 flex w-full max-w-sm -translate-x-1/2 flex-col gap-2 px-4 pointer-events-none md:bottom-6"
      >
        {toasts.map((t) => (
          <Toast key={t.id} toast={t} onDismiss={removeToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}
